<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <ListPageShell>
    <!-- 页面头部 -->
    <template #header>
      <div class="list-page-panel is-header-minimal">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">消息中心</h1>
            <p class="list-page-description">查看和管理您的所有消息通知</p>
          </div>
        </div>
      </div>
    </template>

    <template #filters>
      <!-- 筛选和操作栏 -->
      <PageFilterBar
        body-class="p-4"
        content-class="flex flex-col gap-3"
        :show-refresh="true"
        :refresh-loading="loading"
        @refresh="handleRefresh"
      >
        <div class="flex flex-wrap items-center gap-1 rounded-lg bg-slate-100 p-1">
          <button
            v-for="tab in tabs"
            :key="tab.value"
            class="px-4 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer"
            :class="activeTab === tab.value ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-600 hover:text-slate-800'"
            @click="activeTab = tab.value"
          >
            {{ tab.label }}
            <span
              v-if="tab.count && tab.count !== ''"
              class="status-badge ml-1.5"
              :class="activeTab === tab.value ? 'bg-primary-100 text-primary-700' : 'bg-slate-200 text-slate-600'"
            >
              {{ tab.count }}
            </span>
          </button>
        </div>

        <div class="filter-bar-field relative">
          <svg class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <input
            v-model="searchKeyword"
            type="text"
            placeholder="搜索消息..."
            class="form-search-input form-control-sm w-full"
            @keyup.enter="handleSearch"
          />
        </div>

        <template #actions>
          <button
            v-if="unreadCount > 0"
            type="button"
            class="btn-outline-primary inline-flex items-center gap-2"
            @click="handleMarkAllRead"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
            全部已读
          </button>
        </template>

        <template #searchActions>
          <button
            type="button"
            class="btn-primary"
            :disabled="loading"
            @click="handleSearch"
          >
            搜索
          </button>
          <button
            type="button"
            class="btn-secondary"
            :disabled="loading"
            @click="handleReset"
          >
            重置
          </button>
        </template>
      </PageFilterBar>
    </template>

    <div class="list-page-table-panel">
      <div class="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4 hidden">
        <div class="flex items-center justify-between gap-4 flex-wrap">
          <!-- 标签页切换 -->
          <div class="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
            <button
              v-for="tab in tabs"
              :key="tab.value"
              class="px-4 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer"
              :class="activeTab === tab.value ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-600 hover:text-slate-800'"
              @click="activeTab = tab.value"
            >
              {{ tab.label }}
              <span
                v-if="tab.count && tab.count !== ''"
                class="status-badge ml-1.5"
                :class="activeTab === tab.value ? 'bg-primary-100 text-primary-700' : 'bg-slate-200 text-slate-600'"
              >
                {{ tab.count }}
              </span>
            </button>
          </div>

          <!-- 右侧操作 -->
          <div class="flex items-center gap-3">
            <!-- 搜索框 -->
            <div class="relative">
              <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <input
                v-model="searchKeyword"
                type="text"
                placeholder="搜索消息..."
                class="pl-10 pr-4 py-2 w-64 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <!-- 全部已读按钮 -->
            <button
              v-if="unreadCount > 0"
              class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors cursor-pointer"
              @click="handleMarkAllRead"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
              全部已读
            </button>
          </div>
        </div>
      </div>

      <!-- 消息列表 -->
      <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div v-if="filteredMessages.length > 0" class="divide-y divide-slate-100">
          <div
            v-for="msg in filteredMessages"
            :key="msg.id"
            class="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
            :class="{ 'bg-blue-50/50': !msg.isRead }"
            @click="handleMessageClick(msg)"
          >
            <div class="flex items-start gap-4">
              <!-- 图标 -->
              <div class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" :class="getMessageIconBg(msg.type)">
                <svg class="w-5 h-5" :class="getMessageIconColor(msg.type)" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path v-if="msg.type === 'approval'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  <path v-else-if="msg.type === 'system'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
                </svg>
              </div>

              <!-- 内容 -->
              <div class="flex-1 min-w-0">
                <div class="flex items-start justify-between gap-4">
                  <div class="min-w-0">
                    <div class="flex items-center gap-2">
                      <p class="font-medium text-slate-800 truncate">{{ msg.title }}</p>
                      <span class="status-badge flex-shrink-0" :class="getTypeLabelClass(msg.type)">{{ getTypeLabel(msg.type) }}</span>
                      <span v-if="!msg.isRead" class="status-badge bg-primary-100 text-primary-700 flex-shrink-0">未读</span>
                    </div>
                    <p class="text-sm text-slate-600 mt-1 line-clamp-2">{{ msg.content }}</p>
                    <p class="text-xs text-slate-400 mt-2">{{ msg.time }}</p>
                  </div>
                  <div class="flex items-center gap-2 flex-shrink-0">
                    <!-- 跳转按钮 -->
                    <button
                      v-if="hasNotificationNavigation(msg)"
                      class="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 px-2 py-1 rounded hover:bg-primary-50 transition-colors cursor-pointer"
                      @click.stop="handleNavigationClick(msg)"
                    >
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                      </svg>
                      查看
                    </button>
                    <button
                      v-if="!msg.isRead"
                      class="text-xs text-slate-500 hover:text-primary-600 px-2 py-1 rounded hover:bg-primary-50 transition-colors cursor-pointer"
                      @click.stop="handleMarkAsRead(msg)"
                    >
                      标记已读
                    </button>
                    <button
                      class="text-xs text-slate-400 hover:text-slate-600 px-2 py-1 rounded hover:bg-slate-100 transition-colors cursor-pointer"
                      @click.stop="handleDeleteMessage(msg)"
                    >
                      删除
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-else class="py-16 text-center">
          <svg class="w-16 h-16 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/>
          </svg>
          <p class="text-slate-500 mb-2">{{ searchKeyword ? '没有找到相关消息' : '暂无消息' }}</p>
          <p v-if="searchKeyword" class="text-sm text-slate-400">试试其他关键词</p>
        </div>

        <!-- 分页 -->
        <AppPagination
          v-if="totalCount > 0"
          class="pagination-bridge"
          :current="currentPage"
          :size="pageSize"
          :total="totalCount"
          :page-sizes="[10, 20, 50]"
          :show-page-count="true"
          @page-change="handlePageChange"
          @size-change="handlePageSizeChange"
        />
      </div>
    </div>
  </ListPageShell>

  <MessageDetailModal
    v-model="showDetailModal"
    :message="currentMessage"
    :show-navigation="currentMessage && hasNotificationNavigation(currentMessage)"
    @navigate="handleDetailNavigation"
  />
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import MessageDetailModal from '@/components/MessageDetailModal.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { getNotifications, deleteUserNotification } from '@/api/notification'
import { useMessageStore, typeMap, formatTime } from '@/composables/useMessageStore'
import { useNotificationNavigation } from '@/composables/useNotificationNavigation'

const router = useRouter()
const { hasNotificationNavigation, navigateNotification } = useNotificationNavigation(router)

const {
  unreadCount,
  markAsRead: storeMarkAsRead,
  markAllRead: storeMarkAllRead,
  deleteMessage
} = useMessageStore()

const activeTab = ref('all')
const searchKeyword = ref('')
const currentPage = ref(1)
const pageSize = ref(10)
const total = ref(0)
const loading = ref(false)
const showDetailModal = ref(false)
const currentMessage = ref(null)

const messageList = ref([])

// 加载消息列表
async function loadMessages() {
  try {
    loading.value = true
    const params = {
      pageNum: currentPage.value,
      pageSize: pageSize.value
    }

    // 处理类型筛选
    if (activeTab.value === 'approval') {
      params.type = 'APPROVAL,TASK'
    } else if (activeTab.value === 'system') {
      params.type = 'SYSTEM'
    }

    const res = await getNotifications(params)
    messageList.value = (res.records || []).map(item => ({
      id: item.id,
      type: typeMap[item.type] || 'system',
      title: item.title,
      content: item.summary || item.content || '',
      contentHtml: item.content || '',
      time: formatTime(item.createTime),
      isRead: item.read,
      readTime: item.readTime,
      bizType: item.bizType,
      linkType: item.linkType,
      businessId: item.businessId,
      webUrl: item.webUrl,
      webPath: item.webPath,
      webNewWindow: item.webNewWindow,
      extraParams: item.extraParams
    }))
    total.value = res.total || 0
  } catch (error) {
    console.error('加载消息列表失败:', error)
    ElMessage.error(error.message || '加载消息列表失败')
  } finally {
    loading.value = false
  }
}

// 全部消息
const tabs = computed(() => [
  { value: 'all', label: '全部', count: unreadCount.value > 0 ? `${unreadCount.value}+` : '' },
  { value: 'approval', label: '审批' },
  { value: 'system', label: '系统' }
])

// 过滤后的消息
const filteredMessages = computed(() => {
  let result = messageList.value

  // 按标签筛选
  if (activeTab.value === 'approval') {
    result = result.filter(m => m.type === 'approval')
  } else if (activeTab.value === 'system') {
    result = result.filter(m => m.type === 'system')
  }

  // 按关键词搜索
  if (searchKeyword.value) {
    const keyword = searchKeyword.value.toLowerCase()
    result = result.filter(m =>
      m.title.toLowerCase().includes(keyword) ||
      m.content.toLowerCase().includes(keyword)
    )
  }

  return result
})

const totalCount = computed(() => filteredMessages.value.length)

function getMessageIconBg(type) {
  const bgMap = {
    approval: 'bg-amber-100',
    system: 'bg-blue-100'
  }
  return bgMap[type] || 'bg-slate-100'
}

function getMessageIconColor(type) {
  const colorMap = {
    approval: 'text-amber-600',
    system: 'text-blue-600'
  }
  return colorMap[type] || 'text-slate-600'
}

function getTypeLabel(type) {
  const labelMap = {
    approval: '审批',
    system: '系统'
  }
  return labelMap[type] || '消息'
}

function getTypeLabelClass(type) {
  const classMap = {
    approval: 'bg-amber-100 text-amber-700',
    system: 'bg-blue-100 text-blue-700'
  }
  return classMap[type] || 'bg-slate-100 text-slate-700'
}

// 标记全部已读
function handleSearch() {
  currentPage.value = 1
  loadMessages()
}

function handleReset() {
  searchKeyword.value = ''
  currentPage.value = 1
  loadMessages()
}

function handleRefresh() {
  loadMessages()
}

async function handleMarkAllRead() {
  try {
    await storeMarkAllRead()
    ElMessage.success('已全部标记为已读')
    await loadMessages()
  } catch (error) {
    console.error('标记全部已读失败:', error)
    ElMessage.error(error.message || '操作失败')
  }
}

// 标记单条已读
async function handleMarkAsRead(msg) {
  if (msg.isRead) return
  try {
    await storeMarkAsRead(msg.id)
    msg.isRead = true
  } catch (error) {
    console.error('标记已读失败:', error)
  }
}

// 删除消息
async function handleDeleteMessage(msg) {
  try {
    await deleteUserNotification(msg.id)
    // 从本地列表移除
    const index = messageList.value.findIndex(m => m.id === msg.id)
    if (index > -1) {
      messageList.value.splice(index, 1)
    }
    // 同步更新全局状态
    deleteMessage(msg.id)
    ElMessage.success('删除成功')
  } catch (error) {
    console.error('删除消息失败:', error)
    ElMessage.error(error.message || '删除失败')
  }
}

// 点击跳转按钮
async function handleNavigationClick(msg) {
  // 如果未读，先标记已读
  if (!msg.isRead) {
    await handleMarkAsRead(msg)
  }
  // 执行跳转
  navigateNotification(msg)
}

async function handleDetailNavigation(msg) {
  showDetailModal.value = false
  await handleNavigationClick(msg)
}

// 点击消息（只标记已读和显示详情，不跳转）
async function handleMessageClick(msg) {
  // 标记已读
  await handleMarkAsRead(msg)

  // 如果有富文本内容，显示详情弹窗
  if (msg.contentHtml) {
    currentMessage.value = msg
    showDetailModal.value = true
  }
}

// 标记是否正在切换标签
let isTabChanging = false

const handlePageChange = (page) => {
  currentPage.value = page
}

const handlePageSizeChange = (size) => {
  pageSize.value = size
  currentPage.value = 1
}

// 监听页码变化
watch(currentPage, (oldPage) => {
  // 避免首次加载时重复触发（oldPage 为 undefined 是 Vue 初始化）
  // 避免与 activeTab 重置页码时重复触发
  if (oldPage !== undefined && oldPage !== null && !isTabChanging) {
    loadMessages()
  }
  isTabChanging = false
})

// 监听标签切换
watch(activeTab, () => {
  isTabChanging = true
  currentPage.value = 1
  loadMessages()
})

// 监听搜索关键词变化（防抖）
let searchTimeout
watch(searchKeyword, () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    loadMessages()
  }, 300)
})

onMounted(() => {
  loadMessages()
})
</script>
